import {
  addDays, addMonths, eachDayOfInterval, endOfMonth, endOfWeek, format, parseISO, startOfMonth, startOfWeek,
} from "date-fns";

export const BUSINESS_TZ = "America/New_York";

export const businessToday = () =>
  new Intl.DateTimeFormat("en-CA", { timeZone: BUSINESS_TZ, year: "numeric", month: "2-digit", day: "2-digit" })
    .format(new Date());

export const ymd = (d) => format(d, "yyyy-MM-dd");
export const toDate = (s) => parseISO(s);

export const fmtTime = (t) => {
  if (!t) return "TBC";
  const m = /^(\d{1,2}):(\d{2})/.exec(t);
  if (!m) return t;
  if (/[AP]\.?M/i.test(t)) return `${Number(m[1])}:${m[2]} ${/P/i.test(t) ? "PM" : "AM"}`;
  const h = Number(m[1]);
  return `${((h + 11) % 12) + 1}:${m[2]} ${h >= 12 ? "PM" : "AM"}`;
};

// '02:00 PM' or '14:00' -> '14:00' (for native time inputs)
export const to24h = (t) => {
  const m = /^(\d{1,2}):(\d{2})\s*([AP])?\.?M?/i.exec(t || "");
  if (!m) return "";
  let h = Number(m[1]) % (m[3] ? 12 : 24);
  if (m[3] && m[3].toUpperCase() === "P") h += 12;
  return `${String(h).padStart(2, "0")}:${m[2]}`;
};

export const CHECKBOX_CLS = "rounded-[4px] border-slate-500 data-[state=checked]:bg-cyan-600 data-[state=checked]:border-cyan-600 data-[state=checked]:text-white";
export const SWITCH_CLS = "data-[state=checked]:bg-cyan-600 data-[state=unchecked]:bg-slate-300";

export const fmtDateLong = (s) => format(parseISO(s), "EEE, MMM d, yyyy");
export const fmtStamp = (iso) =>
  new Date(iso).toLocaleString("en-US", { timeZone: BUSINESS_TZ, month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " ET";

export const rangeFor = (view, anchor) => {
  if (view === "day") return { start: anchor, end: anchor };
  if (view === "week") return { start: startOfWeek(anchor), end: endOfWeek(anchor) };
  if (view === "month") return { start: startOfWeek(startOfMonth(anchor)), end: endOfWeek(endOfMonth(anchor)) };
  return { start: anchor, end: addDays(anchor, 30) };
};

export const daysIn = ({ start, end }) => eachDayOfInterval({ start, end });

export const shiftAnchor = (view, anchor, dir) =>
  view === "month" ? addMonths(anchor, dir) : addDays(anchor, dir * (view === "week" ? 7 : view === "list" ? 30 : 1));

export const titleFor = (view, anchor) => {
  if (view === "month") return format(anchor, "MMMM yyyy");
  if (view === "week") {
    const { start, end } = rangeFor("week", anchor);
    return `${format(start, "MMM d")} – ${format(end, "MMM d, yyyy")}`;
  }
  if (view === "list") return `Upcoming from ${format(anchor, "MMM d")}`;
  return format(anchor, "EEEE, MMM d, yyyy");
};

export const groupByDate = (events) =>
  events.reduce((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});

export const STATUS_STYLE = {
  approved: { label: "Approved", light: "bg-blue-100 text-blue-800 border-blue-200", dark: "bg-blue-500/20 text-blue-300 border-blue-500/30" },
  scheduled: { label: "Scheduled", light: "bg-purple-100 text-purple-800 border-purple-200", dark: "bg-purple-500/20 text-purple-300 border-purple-500/30" },
  in_progress: { label: "In Progress", light: "bg-indigo-100 text-indigo-800 border-indigo-200", dark: "bg-indigo-500/20 text-indigo-300 border-indigo-500/30" },
  arrived: { label: "Arrived", light: "bg-lime-100 text-lime-800 border-lime-200", dark: "bg-lime-500/20 text-lime-300 border-lime-500/30" },
  completed: { label: "Completed", light: "bg-green-100 text-green-800 border-green-200", dark: "bg-green-500/20 text-green-300 border-green-500/30" },
  cancelled: { label: "Cancelled", light: "bg-red-100 text-red-800 border-red-200", dark: "bg-red-500/20 text-red-300 border-red-500/30" },
  requested: { label: "Pending Approval", light: "bg-orange-100 text-orange-800 border-orange-200", dark: "bg-orange-500/20 text-orange-300 border-orange-500/30" },
};

export const statusClass = (status, variant) => (STATUS_STYLE[status] || STATUS_STYLE.approved)[variant === "dark" ? "dark" : "light"];

export const THEME = {
  light: { card: "bg-white border-slate-200 text-slate-900", muted: "text-slate-500", sub: "bg-slate-50 border-slate-200", accent: "text-cyan-700", hover: "hover:bg-slate-50", btn: "border-slate-300 text-slate-700 hover:bg-slate-100", active: "bg-slate-900 text-white" },
  dark: { card: "bg-slate-900 border-slate-800 text-white", muted: "text-slate-400", sub: "bg-slate-800/60 border-slate-700", accent: "text-lime-400", hover: "hover:bg-slate-800", btn: "border-slate-700 text-slate-300 hover:bg-slate-800", active: "bg-lime-400 text-slate-900" },
};

export const errorText = (e, fallback = "Something went wrong") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (d.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => x.msg).join(", ");
  return fallback;
};

export const tripTypeLabel = (ride) => {
  const parts = [ride.service_type === "round_trip" ? "Round-Trip" : "One-Way"];
  if (ride.wait_and_return || ride.service_type === "wait_return") parts.push("Wait & Return");
  if (ride.has_multiple_stops || (ride.stops || []).some((s) => s.address)) parts.push("Multiple Stops");
  return parts.join(" · ");
};

export const returnLabel = (ride) => {
  if (ride.service_type !== "round_trip" || ride.wait_and_return) return null;
  if (ride.return_time_tbc || !ride.return_pickup_time) return "Return Time To Be Confirmed";
  const d = ride.return_pickup_date && ride.return_pickup_date !== ride.pickup_date ? `${ride.return_pickup_date} ` : "";
  return `${d}${fmtTime(ride.return_pickup_time)}`;
};
